// Shared plumbing for the FX lane: a GPU ring buffer of interleaved instance data (spawning
// writes a few floats and uploads only the dirty range), the camera-quad geometry, the haze
// as seen by effects, and premultiplied blending so additive glows and alpha smoke share
// one draw call (additive = alpha 0, smoke = normal alpha).

import * as THREE from 'three';
import { HAZE } from '../materials';

export class Ring {
  readonly arr: Float32Array;
  readonly buf: THREE.InstancedInterleavedBuffer;
  head = 0;
  private start = 0;
  private n = 0;
  private touched = new Int32Array(24);
  private nTouched = 0;
  private full = false;
  private ranges: { start: number; count: number }[] = [];
  constructor(readonly cap: number, readonly stride: number) {
    this.arr = new Float32Array(cap * stride);
    this.buf = new THREE.InstancedInterleavedBuffer(this.arr, stride, 1);
    this.buf.setUsage(THREE.DynamicDrawUsage);
    for (let i = 0; i < 40; i++) this.ranges.push({ start: 0, count: 0 });
    this.buf.onUpload(() => { this.pendingFull = false; });
  }
  /** Next slot; returns the float offset to write `stride` floats at. */
  alloc(): number {
    const i = this.head;
    if (this.n === 0) this.start = i;
    this.n++;
    this.head = i + 1 === this.cap ? 0 : i + 1;
    return i * this.stride;
  }
  /** Mark an arbitrary slot (not from alloc) dirty. */
  touch(slot: number) {
    if (this.nTouched < this.touched.length) this.touched[this.nTouched++] = slot;
    else this.full = true;
  }
  attr(size: number, offset: number) {
    return new THREE.InterleavedBufferAttribute(this.buf, size, offset);
  }
  /** set while a full upload is queued and the renderer hasn't consumed it yet */
  private pendingFull = false;
  /**
   * Queue this frame's writes for upload. Ranges accumulate until the renderer uploads them
   * (it clears them), so several update() calls between two renders lose nothing.
   */
  flush() {
    if (this.n === 0 && this.nTouched === 0 && !this.full) return;
    const b = this.buf;
    const s = this.stride;
    const need = (this.n > 0 ? 2 : 0) + this.nTouched;
    let k = b.updateRanges.length;
    if (this.pendingFull || this.full || this.n >= this.cap || k + need > this.ranges.length) {
      // full upload: empty updateRanges + needsUpdate
      b.updateRanges.length = 0;
      this.pendingFull = true;
    } else {
      if (this.n > 0) {
        const end = this.start + this.n;
        if (end <= this.cap) {
          const r = this.ranges[k++]; r.start = this.start * s; r.count = this.n * s; b.updateRanges.push(r);
        } else {
          const r = this.ranges[k++]; r.start = this.start * s; r.count = (this.cap - this.start) * s; b.updateRanges.push(r);
          const r2 = this.ranges[k++]; r2.start = 0; r2.count = (end - this.cap) * s; b.updateRanges.push(r2);
        }
      }
      for (let i = 0; i < this.nTouched; i++) {
        const r = this.ranges[k++]; r.start = this.touched[i] * s; r.count = s; b.updateRanges.push(r);
      }
    }
    b.needsUpdate = true;
    this.n = 0; this.nTouched = 0; this.full = false;
  }
}

/** A unit quad (-0.5..0.5 in xy) as instanced geometry. */
export function quadGeometry(count: number) {
  const g = new THREE.InstancedBufferGeometry();
  g.setAttribute('position', new THREE.BufferAttribute(new Float32Array([-0.5, -0.5, 0, 0.5, -0.5, 0, 0.5, 0.5, 0, -0.5, 0.5, 0]), 3));
  g.setIndex([0, 1, 2, 0, 2, 3]);
  g.instanceCount = count;
  return g;
}

/** Haze uniforms for effect shaders: the same objects as the world's, so it restyles with it. */
export function hazeUniforms() {
  return {
    uHazeColor: HAZE.uHazeColor, uSunHazeColor: HAZE.uSunHazeColor, uLowHazeColor: HAZE.uLowHazeColor,
    uSunDir: HAZE.uSunDir, uHazeDensity: HAZE.uHazeDensity, uHazeBase: HAZE.uHazeBase,
    uHazeHeightFalloff: HAZE.uHazeHeightFalloff, uHazeLow: HAZE.uHazeLow, uNight: HAZE.uNight,
  };
}

/** Same curve as materials.ts applyHaze, evaluated per vertex at the effect's centre. */
export const HAZE_VS = /* glsl */ `
uniform vec3 uHazeColor;
uniform vec3 uSunHazeColor;
uniform vec3 uLowHazeColor;
uniform vec3 uSunDir;
uniform float uHazeDensity;
uniform float uHazeBase;
uniform float uHazeHeightFalloff;
uniform float uHazeLow;
float hazeAt( vec3 wp, out vec3 hz ) {
  vec3 toFrag = wp - cameraPosition;
  float dist = length( toFrag );
  float fd = 1.0 - exp( -pow( dist * uHazeDensity, 1.45 ) );
  float hf = exp( -max( wp.y - uHazeBase, 0.0 ) * uHazeHeightFalloff );
  float f = fd * ( 0.6 + 0.4 * hf ) + hf * uHazeLow * smoothstep( 25.0, 160.0, dist ) * 0.55;
  float sunAmt = pow( max( dot( toFrag / max( dist, 1e-3 ), uSunDir ), 0.0 ), 5.0 );
  hz = mix( mix( uHazeColor, uLowHazeColor, hf * 0.6 ), uSunHazeColor, sunAmt * 0.8 );
  return clamp( f, 0.0, 1.0 );
}
`;

export const HASH_GLSL = /* glsl */ `
float h11( float p ) { return fract( sin( p * 127.1 ) * 43758.5453 ); }
float h21( vec2 p ) { return fract( sin( dot( p, vec2( 127.1, 311.7 ) ) ) * 43758.5453 ); }
float vnoise( vec2 p ) {
  vec2 i = floor( p ), f = fract( p );
  f = f * f * ( 3.0 - 2.0 * f );
  return mix( mix( h21( i ), h21( i + vec2( 1.0, 0.0 ) ), f.x ), mix( h21( i + vec2( 0.0, 1.0 ) ), h21( i + vec2( 1.0, 1.0 ) ), f.x ), f.y );
}
`;

/** Premultiplied: rgb*a + dst*(1-a). Write a = 0 for additive light, a = coverage for smoke. */
export function premulMaterial(m: THREE.ShaderMaterial) {
  m.transparent = true;
  m.depthWrite = false;
  m.blending = THREE.CustomBlending;
  m.blendSrc = THREE.OneFactor;
  m.blendDst = THREE.OneMinusSrcAlphaFactor;
  m.blendSrcAlpha = THREE.OneFactor;
  m.blendDstAlpha = THREE.OneMinusSrcAlphaFactor;
  m.premultipliedAlpha = true;
  return m;
}

const _c = new THREE.Color();
/** Linear rgb from an sRGB hex (THREE.Color converts to the working space). */
export function lin(hex: number, out: Float32Array | number[], o: number, k = 1) {
  _c.setHex(hex);
  out[o] = _c.r * k; out[o + 1] = _c.g * k; out[o + 2] = _c.b * k;
}
export function linRGB(hex: number) { _c.setHex(hex); return [_c.r, _c.g, _c.b] as [number, number, number]; }

// ---------------------------------------------------------------- shared FX atlas
// public/assets/fx/fx_atlas.png (generated by _src/make_atlas.py): 4x4 tiles of 256 px, RGB data.
//   0-3 smoke puffs (R density, GB normal), 4-7 flame tongues (R density, G heat, B licks),
//   8 glow, 9 flares, 10 rings, 11 scorch, 12 frost/ice, 13 acid, 14 crackle, 15 splash.
let _atlas: THREE.Texture | null = null;
export function fxAtlas(): THREE.Texture {
  if (_atlas) return _atlas;
  if (typeof document === 'undefined') { _atlas = new THREE.Texture(); return _atlas; }
  const base = (import.meta as any).env?.BASE_URL ?? '/';
  const t = new THREE.TextureLoader().load(base + 'assets/fx/fx_atlas.png');
  t.colorSpace = THREE.NoColorSpace;
  t.minFilter = THREE.LinearMipmapLinearFilter; t.magFilter = THREE.LinearFilter;
  t.generateMipmaps = true;
  _atlas = t;
  return t;
}

/** GLSL: sample tile i (0..15) of the atlas at local uv p in -1..1 (y up). */
export const ATLAS_GLSL = /* glsl */ `
uniform sampler2D tAtlas;
vec3 tile( float i, vec2 p ) {
  vec2 uv = clamp( p * 0.5 + 0.5, 0.004, 0.996 );
  float c = mod( i, 4.0 ), r = floor( i / 4.0 );
  return texture2D( tAtlas, vec2( ( c + uv.x ) * 0.25, 1.0 - ( r + 1.0 - uv.y ) * 0.25 ) ).rgb;
}
`;
